<script lang="ts">
  import { cubicIn } from "svelte/easing";
  import { sceneGradient, type HueSceneDto } from "@piano/shared";
  import { hue } from "../../lib/stores/hue.svelte.js";
  import Icon from "../Icon.svelte";
  import Dimmer from "./Dimmer.svelte";
  import SceneTile from "./SceneTile.svelte";

  /**
   * Licht-Panel über dem Viewer-Dock: angeheftete Szenen (nach Raum), Raum-Helligkeit/Aus
   * und einzelne Lampen. `onpicked`: nach erfolgreicher Szenenwahl (Viewer schließt dann).
   */
  let { onpicked, anchor }: { onpicked: () => void; anchor?: HTMLElement | undefined } = $props();

  const dock = $derived(hue.dock);
  const active = $derived(hue.activeScene);
  const reachable = $derived(hue.status?.bridge?.reachable !== false);
  const title = $derived(dock.groups.length === 1 ? dock.groups[0]!.name : "Licht");

  async function pick(scene: HueSceneDto): Promise<void> {
    if (await hue.recall(scene)) onpicked();
  }

  // --- Motion ---------------------------------------------------------------------------
  // Das Panel wächst aus dem Licht-Knopf und zieht sich beim Schließen wieder hinein; die
  // Inhalte folgen nacheinander (Kopf → Szenen als diagonale Welle → Regler → Lampen).

  const reduced = () => matchMedia("(prefers-reduced-motion: reduce)").matches;

  /** Federnd mit sanftem Überschwingen (≈ 3 %). */
  const spring = (t: number) => 1 + 2.2 * Math.pow(t - 1, 3) + 1.2 * Math.pow(t - 1, 2);

  /** Ursprung der Animation = Mitte des Licht-Knopfs, relativ zum Panel. */
  function originAt(node: HTMLElement): void {
    const button = anchor?.getBoundingClientRect();
    if (!button) return;
    const panel = node.getBoundingClientRect();
    node.style.transformOrigin = `${button.left + button.width / 2 - panel.left}px ${button.top + button.height / 2 - panel.top}px`;
  }

  function grow(node: HTMLElement) {
    if (reduced()) return { duration: 160, css: (t: number) => `opacity: ${t}` };
    originAt(node);
    return {
      duration: 480,
      easing: spring,
      css: (t: number) => `opacity: ${Math.min(1, t * 3)}; transform: scale(${0.45 + 0.55 * t});`,
    };
  }

  function shrink(node: HTMLElement) {
    if (reduced()) return { duration: 120, css: (t: number) => `opacity: ${t}` };
    originAt(node);
    return {
      duration: 240,
      easing: cubicIn,
      css: (t: number) => `opacity: ${t}; transform: scale(${0.45 + 0.55 * t});`,
    };
  }

  /** Verzögerung je Schritt; ab Schritt 12 kommt alles Weitere gemeinsam. */
  const delay = (step: number) => `${110 + Math.min(step, 12) * 42}ms`;

  /** Reihenfolge des Erscheinens; die Kacheln laufen als Welle (Zeile + Spalte). */
  const layout = $derived.by(() => {
    let step = 1;
    const groups = dock.groups.map((group) => {
      const labelStep = step;
      if (dock.groups.length > 1) step++;
      const scenes = dock.scenes.filter((s) => s.groupId === group.id);
      const tiles = scenes.map((scene, i) => ({ scene, step: step + Math.floor(i / 3) + (i % 3) }));
      if (scenes.length) step += Math.ceil(scenes.length / 3) + 2;
      return { group, labelStep, tiles, roomStep: step++ };
    });
    const lampsStep = step++;
    const lamps = dock.lights.map((light, i) => ({ light, step: step + i }));
    return { groups, lampsStep, lamps };
  });
</script>

<div class="panel" role="dialog" aria-label="Licht" in:grow out:shrink>
  <header class="stagger" style:--delay={delay(0)}>
    <span
      class="orb"
      class:off={!active}
      style:background={active ? sceneGradient(active.colors) : null}
      style:--glow={active?.colors[0] ?? "transparent"}><Icon name="bulb" size={20} /></span
    >
    <span class="head">
      <strong>{title}</strong>
      <span class="t3">Philips Hue · {active ? `Szene „${active.name}“` : "keine Szene aktiv"}</span>
    </span>
  </header>

  {#if !reachable}
    <p class="warn">Die Bridge ist gerade nicht erreichbar.</p>
  {/if}

  {#each layout.groups as { group, labelStep, tiles, roomStep } (group.id)}
    <section>
      {#if dock.groups.length > 1}<span class="label stagger" style:--delay={delay(labelStep)}
          >{group.name}</span
        >{/if}
      <div class="grid">
        {#each tiles as { scene, step } (scene.id)}
          <div class="stagger" style:--delay={delay(step)}>
            <SceneTile {scene} compact onclick={() => pick(scene)} />
          </div>
        {/each}
      </div>
      <div class="room stagger" style:--delay={delay(roomStep)}>
        <Icon name="bulb" size={18} />
        <Dimmer
          value={group.on ? group.brightness : null}
          disabled={!group.groupedLightId}
          label="Helligkeit {group.name}"
          onchange={(value) => hue.setGroupBrightness(group, value)}
        />
        <button
          type="button"
          class="chip"
          class:lit={group.on}
          aria-pressed={!group.on}
          onclick={() => hue.setGroupOn(group, !group.on)}
          ><Icon name="power" size={16} />{group.on ? "Aus" : "An"}</button
        >
      </div>
    </section>
  {/each}

  {#if dock.lights.length}
    <section>
      <span class="label stagger" style:--delay={delay(layout.lampsStep)}>Lampen</span>
      {#each layout.lamps as { light, step } (light.id)}
        <div class="lamp stagger" style:--delay={delay(step)}>
          <span class="lamp-name">
            <span
              class="status"
              style:background={light.on ? (light.color ?? "#FFD9A8") : null}
              style:--glow={light.on ? (light.color ?? "#FFD9A8") : "transparent"}
            ></span>
            <span class="ellipsis">{light.name}</span>
          </span>
          <Dimmer
            value={light.on ? light.brightness : null}
            disabled={!light.on || light.brightness === null}
            label="Helligkeit {light.name}"
            onchange={(value) => hue.setLightBrightness(light, value)}
          />
          <button
            type="button"
            class="toggle"
            role="switch"
            aria-checked={light.on}
            aria-label="{light.name} {light.on ? 'ausschalten' : 'einschalten'}"
            onclick={() => hue.setLightOn(light, !light.on)}
            ><span class="switch" aria-checked={light.on}></span></button
          >
        </div>
      {/each}
    </section>
  {/if}
</div>

<style>
  .panel {
    --w: min(380px, calc(100vw - 24px));
    position: absolute;
    left: 50%;
    /* zentriert ohne transform – der gehört der Wachsen/Schrumpfen-Animation */
    margin-left: calc(var(--w) / -2);
    bottom: calc(96px + var(--safe-bottom));
    width: var(--w);
    max-height: calc(100dvh - 150px - var(--safe-top) - var(--safe-bottom));
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 18px;
    border-radius: var(--r-xl);
    background: var(--raised);
    box-shadow: var(--shadow-2);
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  /* Gestaffeltes Erscheinen: weich einblenden, ein Stück hochgleiten, Unschärfe lösen. */
  .stagger {
    animation: stagger-in 520ms var(--ease-out) both;
    animation-delay: var(--delay, 0ms);
  }
  @keyframes stagger-in {
    from {
      opacity: 0;
      transform: translateY(12px) scale(0.97);
      filter: blur(6px);
    }
  }
  header .orb {
    animation: orb-pop 560ms var(--ease-pop) 140ms both;
  }
  @keyframes orb-pop {
    from {
      transform: scale(0.4) rotate(-20deg);
      opacity: 0;
      box-shadow: 0 0 0 0 transparent;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .stagger,
    header .orb {
      animation: none;
    }
  }
  header {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .orb {
    width: 40px;
    height: 40px;
    flex: 0 0 auto;
    border-radius: var(--r-md);
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(0, 0, 0, 0.55);
    box-shadow: 0 0 28px -2px var(--glow);
    transition:
      background var(--dur-stage) var(--ease-out),
      box-shadow var(--dur-stage) var(--ease-out);
  }
  .orb.off {
    background: var(--raised-2);
    color: var(--text-3);
  }
  .head {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .head strong {
    font-size: 16px;
  }
  .head .t3 {
    font-size: 12px;
    font-weight: 600;
  }
  .warn {
    margin: 0;
    padding: 8px 12px;
    border-radius: var(--r-md);
    background: rgba(255, 122, 89, 0.12);
    color: var(--accent-2);
    font-size: 13px;
    font-weight: 600;
  }
  section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .label {
    padding: 0 2px;
  }
  .grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
  }
  .room {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 10px;
    padding: 4px 4px 0 6px;
    color: var(--text-2);
  }
  .chip {
    height: 34px;
    padding: 0 12px;
    border-radius: var(--r-pill);
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(255, 255, 255, 0.06);
    font-size: 13px;
    font-weight: 700;
    color: var(--text-2);
    transition:
      background var(--dur-fast),
      transform var(--dur-tap);
  }
  .chip:not(.lit) {
    background: var(--accent-soft);
    color: var(--accent);
  }
  .chip:active {
    transform: scale(0.94);
  }
  .lamp {
    display: grid;
    grid-template-columns: 1fr 120px 44px;
    align-items: center;
    gap: 10px;
    min-height: 44px;
    padding: 0 4px 0 6px;
  }
  .lamp-name {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    font-size: 14px;
    font-weight: 600;
  }
  .status {
    flex: 0 0 auto;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--raised-2);
    box-shadow: 0 0 10px var(--glow);
    transition: background var(--dur-base);
  }
  .toggle {
    display: flex;
    justify-content: flex-end;
  }
</style>
